import React, { Dispatch, SetStateAction, useCallback } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { Box, Button, Divider, Drawer, Typography } from '@mui/material';

import { RootState } from '../../../../app/rootReducer';
import {
  FlexCol,
  FlexRow,
  NoraButton,
  Spacer,
} from '../../../../common/components/v2';
import { Decision } from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import { useDecisionSubmit } from '../../decision/useDecisionSubmit';
import { IDecision } from '../../interfaces/IDecision';
import { IDecisionRequest } from '../../interfaces/IDecisionRequest';
import { IDecisionUpdateRequest } from '../../interfaces/IDecisionUpdateRequest';
import styles from '../styles.module.scss';
import { WaiveFormData } from './useWaiveForm';

const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const SUBMIT_WAIVE_DECISION_BUTTON_NAME = 'waive-decision-drawer-submit-button';

export const ReviewWaive = ({
  applicationId,
  isOpen,
  setIsOpen,
  setIsReview,
  description,
  editedDecision,
  formData,
  isEditLeadingToCLR,
}: {
  applicationId: string;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  description: string;
  editedDecision?: IDecision;
  formData: WaiveFormData;
  isEditLeadingToCLR?: boolean;
}) => {
  const { submitCreate, submitEdit } = useDecisionSubmit(applicationId, 'waive');

  const onCreateWaive = useCallback(() => {
    submitCreate({ description });
    setIsReview(false);
    setIsOpen(false);
  }, [
    description,
    submitCreate,
    setIsReview,
    setIsOpen,
  ]);

  const onEditWaive = useCallback(() => {
    if (!editedDecision) {
      return;
    }
    const decisionUpdate: IDecisionUpdateRequest = {
      decision: {
        decision: Decision.WAIVED,
        decisionType: editedDecision.decisionType,
        waiveDescription: description,
        mibCodes: [],
      },
      reason: formData.updateReason || '',
    };
    submitEdit(editedDecision.id.toString(), decisionUpdate, isEditLeadingToCLR);
    setIsReview(false);
    setIsOpen(false);
  }, [applicationId, editedDecision, description, formData, isEditLeadingToCLR, submitEdit, setIsReview, setIsOpen]);

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">{Resource.WAIVE_DECISION}</Typography>
          <Button onClick={() => setIsReview(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Back
            </Typography>
          </Button>
        </Box>
        <Divider />
        <Box m={3}>
          {!!editedDecision && (
            <>
              <FlexRow>
                <FlexCol xs={12}>
                  <span className={styles.DrawerSubTitle}>
                    {Resource.UPDATE_REASON_HEADER}
                  </span>
                </FlexCol>
              </FlexRow>
              <Spacer height={4} />
              <FlexRow>
                <FlexCol xs={12}>
                  <Typography>{formData.updateReason}</Typography>
                </FlexCol>
              </FlexRow>
              <Spacer height={24} />
            </>
          )}

          <FlexRow>
            <FlexCol xs={12}>
              <span className={styles.DrawerSubTitle}>
                {Resource.WAIVED_DECISION_DESCRIPTION}
              </span>
            </FlexCol>
          </FlexRow>
          <Spacer height={4} />

          <FlexRow>
            <FlexCol xs={12}>
              <Typography>{description}</Typography>
            </FlexCol>
          </FlexRow>
        </Box>
        <NoraButton
          name={SUBMIT_WAIVE_DECISION_BUTTON_NAME}
          className={styles.DrawerButton}
          onClick={editedDecision ? onEditWaive : onCreateWaive}
        >
          Waive Decision
        </NoraButton>
      </Box>
    </Drawer>
  );
};
